import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@ohif/ui';
import { Button } from '../../../../app/src/shared/custom-ui';

type R = { value: string; label: string };

const StudyListPagination = ({
    onChangeNext,
    onChangePrev,
    perPage,
    onChangePerPage,
    pagination,
    currentPage,
}: {
    onChangeNext: (token?: string) => void;
    onChangePrev: (token?: string) => void;
    perPage: number;
    currentPage: number;
    onChangePerPage: (n: number) => void;
    pagination?: { next?: string; prev?: string } | null;
}) => {
    const ranges: R[] = [
        { value: '10', label: '10' },
        { value: '25', label: '25' },
        { value: '50', label: '50' },
        { value: '100', label: '100' },
    ];

    const [selectedRange, setSelectedRange] = useState<R>(
        ranges.find(r => r.value === String(perPage)) || ranges[0]
    );

    const onSelectedRange = (selected: R) => {
        setSelectedRange(selected);
        onChangePerPage(parseInt(selected.value, 10));
    };

    const isPrevEnabled = currentPage > 1;
    const canNext = Boolean(pagination?.next);

    return (
        <div className="flex flex-col items-center justify-between gap-4 py-3 sm:flex-row">
            {/* Left cluster: per-page + label */}
            <div className="flex items-center gap-3">
                <label htmlFor="rows-per-page" className="text-[13px] font-medium" style={{ color: 'var(--text-muted)' }}>
                    Results per page
                </label>
                <div className="relative">
                    <select
                        id="rows-per-page"
                        className="w-[92px] appearance-none rounded-[var(--radius-md)] border py-2 pl-3 pr-8 text-[13px] font-medium shadow-sm outline-none transition-all focus:ring-[3px]"
                        style={{
                            background: 'var(--bg-surface)',
                            borderColor: 'var(--border-subtle)',
                            color: 'var(--text-primary)',
                            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.04)',
                        }}
                        value={selectedRange.value}
                        onChange={e => {
                            const selected = ranges.find(r => r.value === e.target.value);
                            if (selected) onSelectedRange(selected);
                        }}
                        aria-label="Rows per page"
                    >
                        {ranges.map(opt => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                    {/* caret */}
                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }}>
                        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path
                                fillRule="evenodd"
                                d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.24 4.5a.75.75 0 01-1.08 0l-4.24-4.5a.75.75 0 01.02-1.06z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </span>
                </div>
            </div>

            {/* Right cluster: page pill + controls */}
            <div className="flex items-center gap-2.5">
                {/* Page indicator */}
                <div
                    className="inline-flex items-center gap-2 rounded-[var(--radius-md)] border px-3 py-1.5 text-[13px] font-medium shadow-sm"
                    style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-subtle)', color: 'var(--text-secondary)' }}
                >
                    <span className="inline-flex h-2 w-2 rounded-full" style={{ background: 'var(--primary-500)' }} aria-hidden="true" />
                    <span>Page</span>
                    <span
                        className="rounded-md px-1.5 py-0.5 font-semibold"
                        style={{ background: 'var(--bg-inset)', color: 'var(--text-heading)' }}
                    >
                        {currentPage}
                    </span>
                </div>

                {/* Prev / Next buttons */}
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="small"
                        disabled={!isPrevEnabled}
                        onClick={() => onChangePrev(pagination?.prev)}
                        leftIcon={<Icon name="chevron-left" className="h-4 w-4" />}
                        aria-label="Previous page"
                        title="Previous"
                    >
                        Previous
                    </Button>

                    <Button
                        variant="primary"
                        size="small"
                        disabled={!canNext}
                        onClick={() => onChangeNext(pagination?.next)}
                        rightIcon={<Icon name="chevron-right" className="h-4 w-4" />}
                        aria-label="Next page"
                        title="Next"
                    >
                        Next
                    </Button>
                </div>
            </div>
        </div>
    );
};

StudyListPagination.propTypes = {
    onChangeNext: PropTypes.func.isRequired,
    onChangePrev: PropTypes.func.isRequired,
    perPage: PropTypes.number.isRequired,
    currentPage: PropTypes.number.isRequired,
    onChangePerPage: PropTypes.func.isRequired,
    pagination: PropTypes.shape({
        next: PropTypes.string,
        prev: PropTypes.string,
    }),
};

export default StudyListPagination;
